import React from 'react';
import ReactApexChart from 'react-apexcharts';
import dayjs from 'dayjs';
import { TWButton } from '../../../components/ui';
import { DataTableSkeleton } from '../../../data/columns';
import { formatCurrency } from '../../../utils/baseUtils';

// The Overview's two charts (owner, 2026-09-24: "grafik kullanabilir
// miyiz"), on the apexcharts the dashboard already draws with. Each takes
// data the tab already read — the month's finance rows, the trend read —
// so a chart never costs a read of its own beyond the trend's.
//
// One quiet palette, the same in both: slate for gross, blue for our net.
// Animations off: the tab re-renders on every read and a chart that
// replays its entrance each time reads as flicker. No legacy-vs-Connect
// split (owner, 2026-09-24): legacy is on its way out, not a thing to
// compare against.
const GROSS = '#94a3b8';
const NET = '#2563eb';

const base = (over = {}) => ({
  chart: { toolbar: { show: false }, fontFamily: 'inherit', animations: { enabled: false }, ...over.chart },
  dataLabels: { enabled: false },
  grid: { borderColor: '#e2e8f0', strokeDashArray: 3 },
  legend: { position: 'top', horizontalAlign: 'left', fontSize: '12px' },
  tooltip: { y: { formatter: (v) => formatCurrency(v) } },
  ...over,
});

const money = { labels: { formatter: (v) => formatCurrency(v) } };

export const ChartCard = ({ title, hint, loading, error, onRetry, empty, children, className = '' }) => (
  <section
    aria-label={title}
    className={`rounded-md border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800/70 ${className}`}
  >
    <div className="mb-2">
      <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100">{title}</h3>
      {hint ? <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">{hint}</p> : null}
    </div>
    {loading ? (
      <DataTableSkeleton rows={4} headers={[{ label: '', w: 'w-full' }]} />
    ) : error ? (
      <span className="inline-flex items-center gap-2 text-sm text-rose-600 dark:text-rose-400">
        Couldn't load
        <TWButton size="sm" label="Retry" aria-label={`Retry ${title}`} onClick={onRetry} />
      </span>
    ) : empty ? (
      <p className="py-8 text-center text-sm text-slate-500 dark:text-slate-400">{empty}</p>
    ) : (
      children
    )}
  </section>
);

// Gross and our net, one point per calendar month, oldest first.
export const TrendChart = ({ points }) => (
  <ReactApexChart
    type="area"
    height={240}
    series={[
      { name: 'Gross', data: points.map((p) => p.grossAmount) },
      { name: 'Our net', data: points.map((p) => p.ourShare) },
    ]}
    options={base({
      colors: [GROSS, NET],
      stroke: { curve: 'smooth', width: 2 },
      fill: { type: 'gradient', gradient: { opacityFrom: 0.25, opacityTo: 0 } },
      xaxis: { categories: points.map((p) => dayjs(p.month + '-01').format('MMM')), axisBorder: { show: false } },
      yaxis: money,
    })}
  />
);

// This month's biggest companies by gross, with our net beside each.
export const TopCompaniesChart = ({ top }) => (
  <ReactApexChart
    type="bar"
    height={Math.max(160, 40 * top.length + 60)}
    series={[
      { name: 'Gross', data: top.map((t) => t.gross) },
      { name: 'Our net', data: top.map((t) => t.net) },
    ]}
    options={base({
      colors: [GROSS, NET],
      plotOptions: { bar: { horizontal: true, barHeight: '60%', borderRadius: 3 } },
      xaxis: { categories: top.map((t) => t.name), ...money },
      yaxis: { labels: { maxWidth: 160 } },
    })}
  />
);
